import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, WMSTileLayer, GeoJSON, Marker, CircleMarker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { CSSProperties } from 'react';
import type { Layer } from 'leaflet';
import type { Feature, Geometry } from 'geojson';
import styles from '../styles/mapHud.module.css';
import { useTheme } from '../theme/ThemeContext';
import { cartoTileUrl, CARTO_ATTRIBUTION } from '../lib/basemap';

/* ─── Types ──────────────────────────────────────────────── */
interface CityData  { city:string; province:string; latitude:number; longitude:number; contentCount:number; }
interface LibraryItem { id:string; title:string; sourceUrl:string; summary?:string; relevanceReason?:string; tags:string[]; dateCrawled:string; }
interface Weather   { temp:number; condition:string; windspeed:number; code:number; }
interface AlertFeature { id:string; headline:string; event:string; alertType:string; description:string; effective:string; expires:string; locationName:string; province:string; lat:number; lng:number; }
interface WildfireFeature { id:string; name:string; agency:string; status:string; sizeHa:number; startDate:string; lat:number; lng:number; }
interface ProjectDoc { documentId:string; title:string; url:string; }
interface ProjectLocation { city:string; province:string; latitude:number; longitude:number; projects:ProjectDoc[]; }
interface CA { id:string; name:string; province:string; website:string|null; hqCity:string|null; region:string|null; latitude:number|null; longitude:number|null; jurisdiction:Geometry|null; contentCount:number; }
interface CoverageRow { province:string; contentCount:number; projectCount:number; lastCrawled:string|null; }
type ChoroplethMode = 'off' | 'coverage' | 'freshness';

/* ─── Weather helpers ────────────────────────────────────── */
const WMO: Record<number,string> = {0:'Clear Sky',1:'Mainly Clear',2:'Partly Cloudy',3:'Overcast',45:'Foggy',48:'Icy Fog',51:'Light Drizzle',53:'Drizzle',55:'Heavy Drizzle',61:'Light Rain',63:'Rain',65:'Heavy Rain',71:'Light Snow',73:'Snow',75:'Heavy Snow',80:'Showers',81:'Showers',82:'Heavy Showers',95:'Thunderstorm'};
const WMO_ICON: Record<number,string> = {0:'☀',1:'🌤',2:'⛅',3:'☁',45:'🌫',48:'🌫',51:'🌦',53:'🌧',55:'🌧',61:'🌧',63:'🌧',65:'🌧',71:'🌨',73:'❄',75:'❄',80:'🌦',81:'🌧',82:'⛈',95:'⛈'};

/* ─── Alert helpers ──────────────────────────────────────── */
const ALERT_CFG: Record<string,{color:string;symbol:string;label:string}> = {
  warning:   { color:'#ef4444', symbol:'⚠',  label:'WARNING'   },
  watch:     { color:'#f97316', symbol:'◉',  label:'WATCH'     },
  advisory:  { color:'#eab308', symbol:'ℹ',  label:'ADVISORY'  },
  statement: { color:'#60a5fa', symbol:'◌',  label:'STATEMENT' },
  ended:     { color:'#6b7280', symbol:'✓',  label:'ENDED'     },
};
const alertCfg = (t:string) => ALERT_CFG[t?.toLowerCase()] ?? ALERT_CFG.warning;

function geomToLatLng(geom:any):[number,number]|null {
  if (!geom) return null;
  if (geom.type==='Point') return [geom.coordinates[1], geom.coordinates[0]];
  if (geom.type==='Polygon') {
    const c=geom.coordinates[0]; return [c.reduce((s:number,p:number[])=>s+p[1],0)/c.length, c.reduce((s:number,p:number[])=>s+p[0],0)/c.length];
  }
  if (geom.type==='MultiPolygon') {
    const all:number[][]=[];geom.coordinates.forEach((p:number[][][])=>p[0].forEach((c:number[])=>all.push(c)));
    return [all.reduce((s,c)=>s+c[1],0)/all.length, all.reduce((s,c)=>s+c[0],0)/all.length];
  }
  return null;
}

/* ─── Marker icons ───────────────────────────────────────── */
const cityIcon = (count:number, active:boolean) => new L.DivIcon({
  className:'', iconSize:[36,36], iconAnchor:[18,18],
  html:`<div style="width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;font-family:inherit;cursor:pointer;background:${active?'rgba(16,185,129,0.9)':'rgba(10,40,20,0.85)'};border:1.5px solid ${active?'#6ee7b7':'rgba(16,185,129,0.5)'};color:${active?'#fff':'#6ee7b7'};box-shadow:0 0 ${active?16:8}px ${active?'rgba(16,185,129,0.6)':'rgba(16,185,129,0.3)'};backdrop-filter:blur(8px);">${count>99?'99+':count}</div>`,
});

const FIRE_STATUS: Record<string,{label:string;color:string}> = {
  OC:{ label:'Out of Control', color:'#ef4444' },
  BH:{ label:'Being Held',     color:'#f97316' },
  UC:{ label:'Under Control',  color:'#eab308' },
  EX:{ label:'Extinguished',   color:'#71717a' },
};
const wildfireIcon = (status:string) => {
  const cfg = FIRE_STATUS[status] ?? FIRE_STATUS.OC;
  return new L.DivIcon({
    className:'', iconSize:[28,28], iconAnchor:[14,14],
    html:`<div style="width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;cursor:pointer;background:${cfg.color}25;border:2px solid ${cfg.color};box-shadow:0 0 12px ${cfg.color}80;">🔥</div>`,
  });
};
const alertIcon = (type:string) => {
  const cfg=alertCfg(type);
  return new L.DivIcon({
    className:'', iconSize:[30,30], iconAnchor:[15,15],
    html:`<div style="width:30px;height:30px;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:13px;cursor:pointer;background:${cfg.color}20;border:1.5px solid ${cfg.color}70;box-shadow:0 0 10px ${cfg.color}50;backdrop-filter:blur(8px);">${cfg.symbol}</div>`,
  });
};

// NAM project locations — distinct from city content nodes (amber diamond).
const projectIcon = (count:number, active:boolean) => new L.DivIcon({
  className:'', iconSize:[30,30], iconAnchor:[15,15],
  html:`<div style="width:26px;height:26px;transform:rotate(45deg);display:flex;align-items:center;justify-content:center;border-radius:5px;cursor:pointer;background:${active?'rgba(245,158,11,0.95)':'rgba(120,70,5,0.85)'};border:2px solid ${active?'#fcd34d':'rgba(245,158,11,0.7)'};box-shadow:0 0 ${active?16:9}px rgba(245,158,11,${active?0.7:0.4});backdrop-filter:blur(6px);"><span style="transform:rotate(-45deg);font-size:10px;font-weight:700;color:${active?'#1a1205':'#fcd34d'};font-family:inherit;">${count>9?'9+':count}</span></div>`,
});

/* ─── Choropleth helpers ─────────────────────────────────── */
// GeoJSON uses "Yukon Territory"; our data uses "Yukon". Bridge the two.
const geoNameToProvince = (name:string) => name === 'Yukon Territory' ? 'Yukon' : name;

// Sequential green ramp for coverage (article count, log-scaled against the max).
function coverageColor(count:number, max:number):string {
  if (!count) return 'rgba(120,120,120,0.06)';
  const t = Math.log1p(count) / Math.log1p(max || 1);
  const ramp = ['#064e3b','#065f46','#047857','#059669','#10b981','#34d399'];
  return ramp[Math.min(ramp.length-1, Math.floor(t * ramp.length))];
}
// Freshness: days since last crawl → green (fresh) to red (stale).
function freshnessColor(lastCrawled:string|null):string {
  if (!lastCrawled) return 'rgba(120,120,120,0.06)';
  const days = (Date.now() - new Date(lastCrawled).getTime()) / 86400000;
  if (days <= 30)  return '#34d399';
  if (days <= 90)  return '#a3e635';
  if (days <= 180) return '#facc15';
  if (days <= 365) return '#fb923c';
  return '#ef4444';
}

/* ─── Sub-components ─────────────────────────────────────── */
const FitBounds:React.FC<{cities:CityData[]}> = ({cities}) => {
  const map=useMap();
  useEffect(()=>{if(cities.length>0){const b=L.latLngBounds(cities.map(c=>[c.latitude,c.longitude]));map.fitBounds(b,{padding:[60,60]});}}, [cities,map]);
  return null;
};

const HudStat:React.FC<{label:string;value:string;color:string}> = ({label,value,color}) => (
  <div className={styles.stat}>
    <div className={styles.statLabel}>{label}</div>
    <div className={styles.statValue} style={{color}}>{value}</div>
  </div>
);

const HudCard:React.FC<{label:string;value:string|number;unit:string;color:string;mono?:boolean}> = ({label,value,unit,color,mono}) => (
  <div className={styles.card} style={{border:`1px solid ${color}25`,borderLeft:`3px solid ${color}`}}>
    <div className={styles.cardLabel}>{label}</div>
    <div className={styles.cardValue} style={{fontSize:mono?'12px':'20px',color}}>{value}</div>
    <div className={styles.cardUnit}>{unit}</div>
  </div>
);

const Ticker:React.FC<{cities:CityData[]}> = ({cities}) => {
  const top=[...cities].sort((a,b)=>b.contentCount-a.contentCount).slice(0,8);
  const text=top.map(c=>`📍 ${c.city}, ${c.province}  ·  ${c.contentCount} articles`).join('     ·     ');
  return (
    <div className={styles.ticker}>
      <div className={styles.tickerInner}>
        {text}{'     ·     '}{text}
      </div>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────── */
const MapHUD:React.FC = () => {
  const { theme } = useTheme();
  const [cities,setCities]           = useState<CityData[]>([]);
  const [alerts,setAlerts]           = useState<AlertFeature[]>([]);
  const [selectedCity,setCity]       = useState<CityData|null>(null);
  const [selectedAlert,setAlert]     = useState<AlertFeature|null>(null);
  const [intel,setIntel]             = useState<LibraryItem[]>([]);
  const [intelLoading,setIntelLoad]  = useState(false);
  const [loading,setLoading]         = useState(true);
  const [cityWeather,setCityWeather]  = useState<Weather|null>(null);
  const [activeLayer,setLayer]       = useState('all');
  const [showAlerts,setShowAlerts]   = useState(true);
  // const [wildfires,setWildfires]     = useState<WildfireFeature[]>([]);
  // const [showWildfires,setShowFires] = useState(true);
  // const [showWatersheds,setShowWS]   = useState(false);
  // const [selectedFire,setFire]       = useState<WildfireFeature|null>(null);
  const wildfires:WildfireFeature[]   = [];
  const showWildfires                 = false;
  const showWatersheds                = false;
  const selectedFire = null as WildfireFeature|null;
  const setFire = (_:WildfireFeature|null)=>{};
  const [panelOpen,setPanelOpen]     = useState(false);
  const [clock,setClock]             = useState(new Date());
  const navigate                     = useNavigate();
  // NAM-projects layer
  const [projects,setProjects]       = useState<ProjectLocation[]>([]);
  const [showCAs,setShowCAs]         = useState(false);
  const [cas,setCAs]                 = useState<CA[]>([]);
  const [selectedCA,setCA]           = useState<CA|null>(null);
  const [showProjects,setShowProj]   = useState(true);
  const [selectedProject,setProject] = useState<ProjectLocation|null>(null);
  const [showProtected,setShowProt]  = useState(false);
  // Province choropleth
  const [coverage,setCoverage]       = useState<CoverageRow[]>([]);
  const [provinceGeo,setProvinceGeo] = useState<any|null>(null);
  const [choropleth,setChoropleth]   = useState<ChoroplethMode>('off');

  useEffect(()=>{
    // City data + NAM projects + per-province coverage
    axios.get('/api/map/cities').then(r=>setCities(r.data)).catch(console.error).finally(()=>setLoading(false));
    axios.get('/api/map/projects').then(r=>setProjects(r.data)).catch(()=>{});
    axios.get('/api/map/coverage').then(r=>setCoverage(r.data)).catch(()=>{});
    // CWFIS wildfires — disabled
    // fetch('https://cwfis.cfs.nrcan.gc.ca/geoserver/public/wfs?service=WFS&version=2.0.0&request=GetFeature&typeName=public:activefires&outputFormat=json&srsname=EPSG:4326')
    //   .then(r=>r.json())
    //   .then((fc:any)=>{
    //     const fires:WildfireFeature[] = (fc.features??[]).reduce((acc:WildfireFeature[],f:any)=>{
    //       const g=f.geometry; if(!g) return acc;
    //       const [lng,lat] = g.type==='Point' ? g.coordinates : [0,0];
    //       if(!lat||!lng) return acc;
    //       const p=f.properties??{};
    //       acc.push({ id:f.id??String(Math.random()), name:p.firename??'Unnamed Fire', agency:p.agency??'', status:p.status??'OC', sizeHa:parseFloat(p.size??'0')||0, startDate:p.startdate??'', lat, lng });
    //       return acc;
    //     },[]);
    //     setWildfires(fires);
    //   })
    //   .catch(()=>{});
    // Environment Canada alerts (OGC API, free/public)
    fetch('https://api.weather.gc.ca/collections/weather-alerts/items?f=json')
      .then(r=>r.json())
      .then((fc:any)=>{
        const features:AlertFeature[] = (fc.features??[]).reduce((acc:AlertFeature[],f:any)=>{
          const pos=geomToLatLng(f.geometry);
          if(!pos) return acc;
          const p=f.properties??{};
          acc.push({
            id: f.id??String(Math.random()),
            headline: p.alert_name_en??p.alert_short_name_en??'Weather Alert',
            event: p.alert_short_name_en??p.alert_name_en??'',
            alertType: p.alert_type??'warning',
            description: p.alert_text_en??'',
            effective: p.validity_datetime??p.publication_datetime??'',
            expires: p.expiration_datetime??'',
            locationName: p.feature_name_en??'',
            province: p.province??'',
            lat: pos[0], lng: pos[1],
          });
          return acc;
        },[]);
        setAlerts(features);
      })
      .catch(()=>{});
    const t=setInterval(()=>setClock(new Date()),1000);
    return ()=>clearInterval(t);
  },[]);

  // Lazy-load province polygons the first time the choropleth is enabled.
  useEffect(()=>{
    if(choropleth!=='off' && !provinceGeo){
      axios.get('/data/canada-provinces.geojson').then(r=>setProvinceGeo(r.data)).catch(()=>{});
    }
  },[choropleth,provinceGeo]);

  // Lazy-load Conservation Authorities (entities + jurisdiction polygons) on first enable.
  useEffect(()=>{
    if(showCAs && cas.length===0){
      axios.get('/api/map/conservation-authorities').then(r=>setCAs(r.data)).catch(()=>{});
    }
  },[showCAs,cas.length]);

  // FeatureCollection for the CA jurisdiction polygon layer.
  const caGeo = useMemo(()=>({
    type:'FeatureCollection',
    features: cas.filter(c=>c.jurisdiction).map(c=>({ type:'Feature', properties:{ id:c.id, name:c.name }, geometry:c.jurisdiction })),
  }),[cas]);

  // Deep-link a place into the assistant: navigate to the chat with a seeded prompt.
  const askAssistant=(place:string)=>{
    navigate('/nam-ai',{ state:{ seedPrompt:`Tell me about natural asset management in ${place}. What initiatives, data, or projects exist there?` } });
  };

  const handleWildfireClick=(f:WildfireFeature)=>{ setFire(f); setCity(null); setAlert(null); setProject(null); setIntel([]); setPanelOpen(true); };
  const handleProjectClick=(p:ProjectLocation)=>{ setProject(p); setCity(null); setAlert(null); setFire(null); setCA(null); setIntel([]); setPanelOpen(true); };
  const handleCAClick=(ca:CA)=>{ setCA(ca); setCity(null); setAlert(null); setFire(null); setProject(null); setIntel([]); setPanelOpen(true); };
  const handleCityClick=async(city:CityData)=>{
    setCity(city); setAlert(null); setFire(null); setCA(null); setProject(null); setPanelOpen(true); setIntelLoad(true); setCityWeather(null);
    // Fetch weather for this city
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${city.latitude}&longitude=${city.longitude}&current=temperature_2m,weather_code,wind_speed_10m&timezone=auto`)
      .then(r=>r.json()).then(d=>setCityWeather({temp:Math.round(d.current.temperature_2m),condition:WMO[d.current.weather_code]??'Unknown',windspeed:Math.round(d.current.wind_speed_10m),code:d.current.weather_code??0}))
      .catch(()=>{});
    try { const r=await axios.get(`/api/map/content?city=${encodeURIComponent(city.city)}&province=${encodeURIComponent(city.province)}`); setIntel(r.data); }
    catch { setIntel([]); } finally { setIntelLoad(false); }
  };
  const handleAlertClick=(a:AlertFeature)=>{ setAlert(a); setCity(null); setFire(null); setCA(null); setProject(null); setIntel([]); setPanelOpen(true); };
  const closePanel=()=>{ setPanelOpen(false); setCity(null); setAlert(null); setFire(null); setCA(null); setProject(null); setCityWeather(null); };

  // Choropleth lookup + scale.
  const coverageByProvince = new Map(coverage.map(c=>[c.province,c]));
  const maxCoverage = coverage.reduce((m,c)=>Math.max(m,c.contentCount),0);
  const geoStyle = (feature?:Feature<Geometry,{name:string}>) => {
    const prov = feature ? geoNameToProvince(feature.properties.name) : '';
    const row = coverageByProvince.get(prov);
    const fill = choropleth==='freshness'
      ? freshnessColor(row?.lastCrawled ?? null)
      : coverageColor(row?.contentCount ?? 0, maxCoverage);
    return { fillColor:fill, fillOpacity:0.55, color:'#10b981', weight:0.8, opacity:0.5 };
  };
  const onEachProvince = (feature:Feature<Geometry,{name:string}>, layer:Layer) => {
    const prov = geoNameToProvince(feature.properties.name);
    const row = coverageByProvince.get(prov);
    const last = row?.lastCrawled ? new Date(row.lastCrawled).toLocaleDateString('en-CA') : 'never';
    layer.bindTooltip(
      `<b>${prov}</b><br/>${row?.contentCount ?? 0} articles · ${row?.projectCount ?? 0} projects<br/>last crawled: ${last}`,
      { sticky:true },
    );
  };

  const totalArticles = cities.reduce((s,c)=>s+c.contentCount,0);
  const provinces     = Array.from(new Set(cities.map(c=>c.province))).filter(Boolean);
  const topCity       = [...cities].sort((a,b)=>b.contentCount-a.contentCount)[0];
  const activeAlerts  = alerts.filter(a=>a.alertType!=='ended');

  const LAYERS=[
    {id:'all',     label:'All Content',    color:'#10b981',dot:'●'},
    {id:'water',   label:'Water Resources',color:'#38bdf8',dot:'◆'},
    {id:'forest',  label:'Forest Coverage',color:'#4ade80',dot:'▲'},
    {id:'wetland', label:'Wetlands',       color:'#2dd4bf',dot:'◈'},
    {id:'policy',  label:'Policy Docs',   color:'#a78bfa',dot:'■'},
  ];

  const panelStyle:CSSProperties={ right:panelOpen?0:'-380px' };

  return (
    <div className={styles.root}>

      {/* ── TOP BAR ──────────────────────────────────────── */}
      <div className={styles.topBar}>
        <div className={styles.brand}>
          <div className={styles.brandDot}/>
          <span className={styles.brandText}>NATURESCAN</span>
        </div>
        <div className={styles.vDivider}/>
        <HudStat label="NODES"     value={String(cities.length)}    color="#10b981"/>
        <HudStat label="ARTICLES"  value={String(totalArticles)}    color="#4ade80"/>
        <HudStat label="PROVINCES" value={String(provinces.length)} color="#2dd4bf"/>
        {activeAlerts.length>0 && <HudStat label="ALERTS" value={String(activeAlerts.length)} color="#f97316"/>}
        <div className={styles.spacer}/>
        <div className={styles.clock}>
          {clock.toLocaleTimeString('en-CA',{hour12:false})}
        </div>
      </div>

      {/* ── BODY ─────────────────────────────────────────── */}
      <div className={styles.body}>

        {/* ── LEFT PANEL ─────────────────────────────────── */}
        <div className={styles.leftPanel}>
          <div className={styles.deckHeader}>
            <div className={styles.deckEyebrow}>CONTROL DECK</div>
            <div className={styles.deckTitle}>LAYERS</div>
          </div>
          <div className={styles.layerList}>
            {LAYERS.map(l=>(
              <button key={l.id} onClick={()=>setLayer(l.id)} className={styles.layerBtn} style={{background:activeLayer===l.id?`${l.color}12`:'transparent',border:`1px solid ${activeLayer===l.id?l.color+'40':'transparent'}`}}>
                <span className={styles.layerDot} style={{color:l.color}}>{l.dot}</span>
                <span className={styles.layerLabel} style={{color:activeLayer===l.id?l.color:'var(--text-secondary)'}}>{l.label}</span>
                {activeLayer===l.id && <div className={styles.layerActiveDot} style={{background:l.color}}/>}
              </button>
            ))}
          </div>

          {/* Overlay toggles */}
          <div className={styles.overlaySection}>
            <div className={styles.sectionEyebrow}>OVERLAYS</div>
            <div className={styles.overlayList}>
              <button onClick={()=>setShowProj(p=>!p)} className={styles.overlayBtn} style={{background:showProjects?'rgba(245,158,11,0.1)':'transparent',border:`1px solid ${showProjects?'rgba(245,158,11,0.35)':'transparent'}`}}>
                <span className={styles.overlayIcon}>◆</span>
                <span className={styles.overlayLabel} style={{color:showProjects?'#f59e0b':'var(--text-secondary)'}}>NAM Projects</span>
                <span className={styles.overlayCount} style={{color:showProjects?'#fcd34d':'var(--text-muted)'}}>{projects.length}</span>
              </button>
              <button onClick={()=>setShowProt(p=>!p)} className={styles.overlayBtn} style={{background:showProtected?'rgba(52,211,153,0.1)':'transparent',border:`1px solid ${showProtected?'rgba(52,211,153,0.35)':'transparent'}`}}>
                <span className={styles.overlayIcon}>🛡</span>
                <span className={styles.overlayLabel} style={{color:showProtected?'#34d399':'var(--text-secondary)'}}>Protected Areas</span>
              </button>
              <button onClick={()=>setShowAlerts(p=>!p)} className={styles.overlayBtn} style={{background:showAlerts?'rgba(249,115,22,0.1)':'transparent',border:`1px solid ${showAlerts?'rgba(249,115,22,0.35)':'transparent'}`}}>
                <span className={styles.overlayIcon}>⚠</span>
                <span className={styles.overlayLabel} style={{color:showAlerts?'#f97316':'var(--text-secondary)'}}>EC Alerts</span>
                <span className={styles.overlayCount} style={{color:showAlerts?'#fb923c':'var(--text-muted)'}}>{activeAlerts.length}</span>
              </button>
              <button onClick={()=>setShowCAs(p=>!p)} className={styles.overlayBtn} style={{background:showCAs?'rgba(56,189,248,0.12)':'transparent',border:`1px solid ${showCAs?'rgba(56,189,248,0.4)':'transparent'}`}}>
                <span className={styles.overlayIcon}>🌊</span>
                <span className={styles.overlayLabel} style={{color:showCAs?'#38bdf8':'var(--text-secondary)'}}>Conservation Authorities</span>
                <span className={styles.overlayCount} style={{color:showCAs?'#7dd3fc':'var(--text-muted)'}}>{cas.length||''}</span>
              </button>
              {/* Wildfire toggle — disabled
              <button onClick={()=>setShowFires(p=>!p)} ...>🔥 Wildfires</button>
              <button onClick={()=>setShowWS(p=>!p)} ...>💧 FWA Watersheds</button>
              */}
            </div>
          </div>

          {/* Province choropleth */}
          <div className={styles.overlaySection}>
            <div className={styles.sectionEyebrow}>PROVINCE CHOROPLETH</div>
            <div className={styles.overlayList}>
              {([['off','Off'],['coverage','Coverage'],['freshness','Freshness']] as [ChoroplethMode,string][]).map(([mode,label])=>(
                <button key={mode} onClick={()=>setChoropleth(mode)} className={styles.overlayBtn} style={{background:choropleth===mode?'rgba(16,185,129,0.12)':'transparent',border:`1px solid ${choropleth===mode?'rgba(16,185,129,0.4)':'transparent'}`}}>
                  <span className={styles.overlayLabel} style={{color:choropleth===mode?'#10b981':'var(--text-secondary)'}}>{label}</span>
                </button>
              ))}
            </div>
            {choropleth==='freshness' && (
              <div className={styles.legend}>
                {[['#34d399','< 30 days'],['#a3e635','< 90 days'],['#facc15','< 180 days'],['#fb923c','< 1 year'],['#ef4444','older']].map(([c,l])=>(
                  <div key={l} className={styles.legendRow}>
                    <span className={styles.legendSymbol} style={{color:c}}>■</span>
                    <span className={styles.legendLabel} style={{color:'var(--text-secondary)'}}>{l}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Alert type legend */}
          {showAlerts && activeAlerts.length>0 && (
            <div className={styles.legend}>
              {Object.entries(ALERT_CFG).filter(([k])=>k!=='ended').map(([k,v])=>{
                const n=alerts.filter(a=>a.alertType===k).length;
                return n>0?(
                  <div key={k} className={styles.legendRow}>
                    <span className={styles.legendSymbol}>{v.symbol}</span>
                    <span className={styles.legendLabel} style={{color:v.color}}>{v.label}</span>
                    <span className={styles.legendCount}>{n}</span>
                  </div>
                ):null;
              })}
            </div>
          )}

          {/* Province breakdown */}
          <div className={styles.coverage}>
            <div className={styles.sectionEyebrow}>COVERAGE</div>
            {provinces.slice(0,6).map(p=>{
              const count=cities.filter(c=>c.province===p).reduce((s,c)=>s+c.contentCount,0);
              const pct=totalArticles>0?(count/totalArticles)*100:0;
              return (
                <div key={p} className={styles.coverageRow}>
                  <div className={styles.coverageHead}>
                    <span>{p?.slice(0,18)}</span>
                    <span className={styles.coverageCount}>{count}</span>
                  </div>
                  <div className={styles.coverageTrack}>
                    <div className={styles.coverageBar} style={{width:`${pct}%`}}/>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.spacer}/>
          <div className={styles.sources}>
            TILES · CARTO<br/>
            CONTENT · NATURESCAN DB<br/>
            PROJECTS · NAM REPORTS<br/>
            PROTECTED · CPCAD ECCC<br/>
            ALERTS · ENV CANADA<br/>
            BOUNDARIES · STATCAN
          </div>
        </div>

        {/* ── MAP ────────────────────────────────────────── */}
        <div className={styles.mapCol}>
          {loading?(
            <div className={styles.loadingWrap}>
              <div className={styles.loadingText}>LOADING SPATIAL DATA...</div>
            </div>
          ):(
            <MapContainer center={[56.1304,-106.3468]} zoom={4} className={styles.mapEl}>
              <TileLayer
                key={theme}
                attribution={CARTO_ATTRIBUTION}
                url={cartoTileUrl(theme === 'light' ? 'light_all' : 'dark_all')}
                subdomains="abcd"
                maxZoom={20}
              />
              {showWatersheds && (
                <WMSTileLayer
                  url="https://openmaps.gov.bc.ca/geo/pub/WHSE_BASEMAPPING.FWA_ASSESSMENT_WATERSHEDS_POLY/ows"
                  layers="pub:WHSE_BASEMAPPING.FWA_ASSESSMENT_WATERSHEDS_POLY"
                  format="image/png"
                  transparent={true}
                  opacity={0.4}
                  version="1.1.1"
                  attribution='&copy; <a href="https://www2.gov.bc.ca/">BC Gov</a>'
                />
              )}
              {/* Protected & conserved areas (CPCAD · ECCC) */}
              {showProtected && (
                <WMSTileLayer
                  url="https://maps-cartes.ec.gc.ca/arcgis/services/CWS_SCF/CPCAD/MapServer/WMSServer"
                  layers="0"
                  format="image/png"
                  transparent={true}
                  opacity={0.5}
                  version="1.3.0"
                  attribution='Protected areas &copy; <a href="https://www.canada.ca/en/environment-climate-change.html">ECCC · CPCAD</a>'
                />
              )}
              {/* Province coverage / freshness choropleth */}
              {choropleth!=='off' && provinceGeo && (
                <GeoJSON
                  key={`${choropleth}-${maxCoverage}-${coverage.length}`}
                  data={provinceGeo}
                  style={geoStyle as any}
                  onEachFeature={onEachProvince as any}
                />
              )}
              <FitBounds cities={cities}/>
              {/* City markers */}
              <MarkerClusterGroup disableClusteringAtZoom={6} maxClusterRadius={50} spiderfyOnMaxZoom={false}>
                {cities.map((c,i)=>(
                  <Marker key={i} position={[c.latitude,c.longitude]} icon={cityIcon(c.contentCount,selectedCity?.city===c.city)} eventHandlers={{click:()=>handleCityClick(c)}}>
                    <Popup>
                      <div className={styles.popupBody}>
                        <div className={styles.popupCity}>{c.city}</div>
                        <div className={styles.popupProvince}>{c.province}</div>
                        <div className={styles.popupArticles}>{c.contentCount} articles indexed</div>
                        <div className={styles.popupLink} onClick={()=>handleCityClick(c)}>› VIEW INTEL</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
              {/* NAM project markers */}
              {showProjects && projects.map((p,i)=>(
                <Marker key={`proj-${i}`} position={[p.latitude,p.longitude]} icon={projectIcon(p.projects.length,selectedProject?.city===p.city)} eventHandlers={{click:()=>handleProjectClick(p)}}>
                  <Popup>
                    <div className={styles.popupBody}>
                      <div className={styles.popupCity} style={{color:'#f59e0b'}}>◆ {p.city}</div>
                      <div className={styles.popupProvince}>{p.province}</div>
                      <div className={styles.popupArticles}>{p.projects.length} NAM project{p.projects.length>1?'s':''}</div>
                      <div className={styles.popupLink} style={{color:'#f59e0b'}} onClick={()=>handleProjectClick(p)}>› VIEW PROJECTS</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {/* Conservation Authority jurisdiction polygons + HQ markers */}
              {showCAs && caGeo.features.length>0 && (
                <GeoJSON
                  key={`ca-${cas.length}`}
                  data={caGeo as any}
                  style={()=>({ color:'#38bdf8', weight:1, fillColor:'#38bdf8', fillOpacity:0.12 }) as any}
                  onEachFeature={(feature:any, layer:any)=>{
                    const ca=cas.find(c=>c.id===feature.properties?.id);
                    layer.on('click',()=>{ if(ca) handleCAClick(ca); });
                    if(ca) layer.bindTooltip(ca.name,{sticky:true});
                  }}
                />
              )}
              {showCAs && cas.filter(c=>c.latitude&&c.longitude).map(c=>(
                <CircleMarker key={`ca-hq-${c.id}`} center={[c.latitude as number,c.longitude as number]} radius={6}
                  pathOptions={{color:'#0ea5e9',fillColor:'#38bdf8',fillOpacity:0.9,weight:2}}
                  eventHandlers={{click:()=>handleCAClick(c)}}>
                  <Popup>
                    <div className={styles.popupBody}>
                      <div className={styles.popupCity} style={{color:'#0ea5e9'}}>🌊 {c.name}</div>
                      <div className={styles.popupProvince}>{c.hqCity} · {c.contentCount} item{c.contentCount===1?'':'s'}</div>
                      <div className={styles.popupLink} style={{color:'#0ea5e9'}} onClick={()=>handleCAClick(c)}>› VIEW DETAILS</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
              {/* Wildfire markers */}
              {showWildfires && wildfires.map((f,i)=>(
                <Marker key={`fire-${i}`} position={[f.lat,f.lng]} icon={wildfireIcon(f.status)} eventHandlers={{click:()=>handleWildfireClick(f)}}>
                  <Popup>
                    <div className={styles.popupBodyWide}>
                      <div className={styles.popupFireHead} style={{color:(FIRE_STATUS[f.status]??FIRE_STATUS.OC).color}}>🔥 {(FIRE_STATUS[f.status]??FIRE_STATUS.OC).label}</div>
                      <div className={styles.popupName}>{f.name}</div>
                      <div className={styles.popupMeta}>{f.agency} · {f.sizeHa.toLocaleString()} ha</div>
                      <div className={styles.popupLink} style={{color:(FIRE_STATUS[f.status]??FIRE_STATUS.OC).color,marginTop:'4px'}} onClick={()=>handleWildfireClick(f)}>› VIEW DETAILS</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {/* Alert markers */}
              {showAlerts && alerts.map((a,i)=>{
                const cfg=alertCfg(a.alertType);
                return (
                  <Marker key={`alert-${i}`} position={[a.lat,a.lng]} icon={alertIcon(a.alertType)} eventHandlers={{click:()=>handleAlertClick(a)}}>
                    <Popup>
                      <div className={styles.popupBodyWider}>
                        <div className={styles.popupAlertHead} style={{color:cfg.color}}>{cfg.symbol} {cfg.label}</div>
                        <div className={styles.popupAlertHeadline}>{a.headline}</div>
                        {a.expires && <div className={styles.popupExpires}>Expires: {new Date(a.expires).toLocaleDateString('en-CA')}</div>}
                        <div className={styles.popupLink} style={{color:cfg.color,marginTop:'4px'}} onClick={()=>handleAlertClick(a)}>› VIEW DETAILS</div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}

          {/* Floating stat cards */}
          <div className={styles.cardStack}>
            <HudCard label="ACTIVE NODES"    value={cities.length}  unit="locations" color="#10b981"/>
            <HudCard label="TOTAL INDEXED"   value={totalArticles}  unit="articles"  color="#4ade80"/>
            {topCity && <HudCard label="HIGHEST DENSITY" value={topCity.city} unit={`${topCity.contentCount} articles`} color="#2dd4bf" mono/>}
            {showAlerts && activeAlerts.length>0 && <HudCard label="ACTIVE ALERTS" value={activeAlerts.length} unit="Environment Canada" color="#f97316"/>}
            {showWildfires && wildfires.length>0 && <HudCard label="ACTIVE FIRES" value={wildfires.filter(f=>f.status==='OC').length} unit={`${wildfires.length} total · CWFIS`} color="#ef4444"/>}
          </div>

          {panelOpen && (
            <button onClick={closePanel} className={styles.closeBtn}>
              ✕ CLOSE
            </button>
          )}
        </div>

        {/* ── INTEL PANEL ────────────────────────────────── */}
        <div className={styles.panel} style={panelStyle}>
          {selectedCity && (
            <>
              <div className={styles.panelHeader}>
                <div className={styles.panelEyebrow}>LOCATION INTEL</div>
                <div className={styles.panelTitle}>{selectedCity.city}</div>
                <div className={styles.panelSub}>
                  {selectedCity.province} · {selectedCity.latitude.toFixed(4)}°N · {selectedCity.longitude.toFixed(4)}°W
                </div>
                {/* Weather for this city */}
                {cityWeather ? (
                  <div className={styles.weather}>
                    <span className={styles.weatherIcon}>{WMO_ICON[cityWeather.code]??'🌡'}</span>
                    <div className={styles.weatherMain}>
                      <div className={styles.weatherTemp}>{cityWeather.temp}°C</div>
                      <div className={styles.weatherCond}>{cityWeather.condition}</div>
                    </div>
                    <div className={styles.weatherWindCol}>
                      <div className={styles.weatherWindLabel}>WIND</div>
                      <div className={styles.weatherWindVal}>{cityWeather.windspeed} km/h</div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.weatherLoading}>FETCHING WEATHER...</div>
                )}
                <div className={styles.counter}>
                  <div className={styles.counterValue}>{selectedCity.contentCount}</div>
                  <div className={styles.counterLabel}>ARTICLES INDEXED</div>
                </div>
                <button className={styles.askBtn} onClick={()=>askAssistant(`${selectedCity.city}, ${selectedCity.province}`)}>
                  💬 ASK THE ASSISTANT ABOUT {selectedCity.city.toUpperCase()}
                </button>
                <button className={styles.askBtn} style={{marginTop:6,opacity:0.92}} onClick={()=>navigate(`/entities/profile?type=place&name=${encodeURIComponent(selectedCity.city)}&province=${encodeURIComponent(selectedCity.province)}`)}>
                  📋 VIEW FULL PROFILE
                </button>
              </div>
              <div className={styles.intelScroll}>
                {intelLoading?(
                  <div className={`${styles.intelStatus} ${styles.intelStatusLoading}`}>LOADING INTEL...</div>
                ):intel.length===0?(
                  <div className={`${styles.intelStatus} ${styles.intelStatusEmpty}`}>NO RECORDS FOUND</div>
                ):(
                  <div className={styles.intelList}>
                    {intel.map(item=>(
                      <div key={item.id} className={styles.intelCard}>
                        <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className={styles.intelTitle}>{item.title}</a>
                        {item.summary && <p className={styles.intelSummary}>{item.summary.slice(0,120)}…</p>}
                        {item.tags.length>0 && (
                          <div className={styles.intelTags}>
                            {item.tags.map((t,i)=><span key={i} className={styles.intelTag}>{t.toUpperCase()}</span>)}
                          </div>
                        )}
                        <div className={styles.intelDate}>{new Date(item.dateCrawled).toLocaleDateString('en-CA')}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {selectedAlert && (()=>{
            const cfg=alertCfg(selectedAlert.alertType);
            return (
              <>
                <div className={styles.panelHeader} style={{borderBottom:`1px solid ${cfg.color}30`}}>
                  <div className={styles.panelEyebrow} style={{color:`${cfg.color}99`}}>ENVIRONMENT CANADA · ALERT</div>
                  <div className={styles.detailIconRow}>
                    <div className={styles.detailBadge} style={{background:`${cfg.color}20`,border:`1px solid ${cfg.color}60`}}>{cfg.symbol}</div>
                    <div>
                      <div className={styles.detailLabel} style={{color:cfg.color}}>{cfg.label}</div>
                      <div className={styles.detailEyebrowSub}>{selectedAlert.event}</div>
                    </div>
                  </div>
                  <div className={styles.detailHeadline}>{selectedAlert.headline}</div>
                  {selectedAlert.locationName && (
                    <div className={styles.detailLocation}>📍 {selectedAlert.locationName}{selectedAlert.province ? ` · ${selectedAlert.province}` : ''}</div>
                  )}
                  <div className={styles.detailGrid}>
                    {selectedAlert.effective && (
                      <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>EFFECTIVE</div>
                        <div className={styles.detailCellValue}>{new Date(selectedAlert.effective).toLocaleString('en-CA',{dateStyle:'short',timeStyle:'short'})}</div>
                      </div>
                    )}
                    {selectedAlert.expires && (
                      <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>EXPIRES</div>
                        <div className={styles.detailCellValue}>{new Date(selectedAlert.expires).toLocaleString('en-CA',{dateStyle:'short',timeStyle:'short'})}</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.detailBody}>
                  {selectedAlert.description ? (
                    <div className={styles.detailDescription}>{selectedAlert.description}</div>
                  ) : (
                    <div className={styles.detailEmpty}>NO ADDITIONAL DETAILS</div>
                  )}
                </div>
              </>
            );
          })()}

          {selectedFire && (()=>{
            const cfg = FIRE_STATUS[selectedFire.status] ?? FIRE_STATUS.OC;
            return (
              <>
                <div className={styles.panelHeader} style={{borderBottom:`1px solid ${cfg.color}30`}}>
                  <div className={styles.panelEyebrow} style={{color:`${cfg.color}99`}}>CWFIS · ACTIVE FIRE</div>
                  <div className={styles.detailIconRow}>
                    <div className={styles.detailBadgeRound} style={{background:`${cfg.color}20`,border:`2px solid ${cfg.color}`}}>🔥</div>
                    <div>
                      <div className={styles.detailLabel} style={{color:cfg.color}}>{cfg.label}</div>
                      <div className={styles.detailEyebrowSub}>{selectedFire.agency}</div>
                    </div>
                  </div>
                  <div className={styles.fireName}>{selectedFire.name}</div>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailCell}>
                      <div className={styles.detailCellLabel}>SIZE</div>
                      <div className={styles.fireSizeValue} style={{color:cfg.color}}>{selectedFire.sizeHa.toLocaleString()}<span className={styles.fireSizeUnit}> ha</span></div>
                    </div>
                    {selectedFire.startDate && (
                      <div className={styles.detailCell}>
                        <div className={styles.detailCellLabel}>STARTED</div>
                        <div className={styles.fireDateValue}>{new Date(selectedFire.startDate).toLocaleDateString('en-CA')}</div>
                      </div>
                    )}
                    <div className={styles.detailCell}>
                      <div className={styles.detailCellLabel}>COORDINATES</div>
                      <div className={styles.fireCoordValue}>{selectedFire.lat.toFixed(4)}°N</div>
                      <div className={styles.fireCoordValue}>{Math.abs(selectedFire.lng).toFixed(4)}°W</div>
                    </div>
                  </div>
                </div>
                <div className={styles.fireFooter}>
                  Data sourced from the Canadian<br/>Wildland Fire Information System<br/>(CWFIS · Natural Resources Canada)
                </div>
              </>
            );
          })()}

          {selectedCA && (
            <>
              <div className={styles.panelHeader}>
                <div className={styles.panelEyebrow}>CONSERVATION AUTHORITY</div>
                <div className={styles.panelTitle}>{selectedCA.name}</div>
                <div className={styles.panelSub}>
                  {selectedCA.hqCity ? `HQ: ${selectedCA.hqCity} · ` : ''}{selectedCA.province}
                </div>
                {selectedCA.region && <div className={styles.panelSub} style={{marginTop:6}}>{selectedCA.region}</div>}
                <div className={styles.counter}>
                  <div className={styles.counterValue}>{selectedCA.contentCount}</div>
                  <div className={styles.counterLabel}>ITEMS INDEXED</div>
                </div>
                {selectedCA.website && (
                  <a href={selectedCA.website} target="_blank" rel="noreferrer" className={styles.askBtn} style={{display:'block',textAlign:'center',textDecoration:'none'}}>🌐 VISIT WEBSITE</a>
                )}
                <button className={styles.askBtn} style={{marginTop:6}} onClick={()=>navigate(`/entities/profile?type=org&name=${encodeURIComponent(selectedCA.name)}`)}>
                  📋 VIEW FULL PROFILE
                </button>
                <button className={styles.askBtn} style={{marginTop:6,opacity:0.92}} onClick={()=>askAssistant(selectedCA.name)}>
                  💬 ASK THE ASSISTANT
                </button>
              </div>
            </>
          )}
          {selectedProject && (
            <>
              <div className={styles.panelHeader} style={{borderBottom:'1px solid rgba(245,158,11,0.3)'}}>
                <div className={styles.panelEyebrow} style={{color:'rgba(245,158,11,0.7)'}}>NAM PROJECTS</div>
                <div className={styles.panelTitle}>{selectedProject.city}</div>
                <div className={styles.panelSub}>
                  {selectedProject.province} · {selectedProject.latitude.toFixed(4)}°N · {Math.abs(selectedProject.longitude).toFixed(4)}°W
                </div>
                <div className={styles.counter}>
                  <div className={styles.counterValue} style={{color:'#f59e0b'}}>{selectedProject.projects.length}</div>
                  <div className={styles.counterLabel}>NAM PROJECT DOCUMENTS</div>
                </div>
                <button className={styles.askBtn} onClick={()=>askAssistant(`${selectedProject.city}, ${selectedProject.province}`)}>
                  💬 ASK THE ASSISTANT ABOUT {selectedProject.city.toUpperCase()}
                </button>
              </div>
              <div className={styles.intelScroll}>
                <div className={styles.projectList}>
                  {selectedProject.projects.map(p=>(
                    <div key={p.documentId} className={styles.projectCard}>
                      {p.url
                        ? <a href={p.url} target="_blank" rel="noopener noreferrer" className={styles.projectTitle}>{p.title}</a>
                        : <span className={styles.projectTitle}>{p.title}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {!selectedCity && !selectedAlert && !selectedFire && !selectedProject && !selectedCA && (
            <div className={styles.panelEmpty}>
              SELECT A NODE, PROJECT<br/>OR ALERT ON THE MAP<br/>TO VIEW INTEL
            </div>
          )}
        </div>
      </div>

      {/* ── BOTTOM TICKER ──────────────────────────────────── */}
      <div className={styles.tickerBar}>
        <span className={styles.tickerLive}>▶ LIVE</span>
        <div className={styles.tickerLiveWrap}><Ticker cities={cities}/></div>
      </div>
    </div>
  );
};

export default MapHUD;
